// src/components/Layout.tsx
import React, { PropsWithChildren, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/David.svg';

const Layout: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const isHome = location.pathname === '/';

  // Function to scroll to a section with offset for the fixed header
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Get the header height (approximately 112px based on h-24 + padding)
      const headerHeight = 112;

      // Calculate the element's position relative to the viewport
      const elementPosition = element.getBoundingClientRect().top;

      // Calculate the absolute position and adjust for header height
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20; // Added extra 20px padding

      // Scroll to the adjusted position
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  // Check for stored scroll target on home page load
  useEffect(() => {
    if (isHome) {
      const scrollTarget = localStorage.getItem('scrollTarget');
      if (scrollTarget) {
        // Clear the stored target to prevent scrolling on future page loads
        localStorage.removeItem('scrollTarget');

        // Add a small delay to ensure the page is fully loaded
        setTimeout(() => {
          scrollToSection(scrollTarget);
        }, 100);
      }
    }
  }, [isHome, scrollToSection]);

  // Handle section navigation - either scroll or navigate to home first
  const handleSectionNavigation = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    
    if (isHome) {
      // If already on home page, just scroll to the section
      scrollToSection(sectionId);
    } else {
      // If on another page, go to home page first
      localStorage.setItem('scrollTarget', sectionId);
      window.location.href = '/'; // Use direct browser navigation instead of React Router
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0B0F1A] text-white">
      <nav className="fixed w-full bg-black z-50">
        <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center">
          <a href="/" className="flex items-center">
            <img src={logo} alt="David Sheeshka" className="h-24 w-auto" />
          </a>
          <div className="flex gap-8 text-sm font-medium">
            <a 
              href="/" 
              className="hover:text-blue-500 transition-colors"
            >
              Home
            </a>
            <Link to="/blog" className="hover:text-blue-500 transition-colors">Blog</Link>
            <a 
              href="/#about" 
              onClick={(e) => handleSectionNavigation(e, 'about')} 
              className="hover:text-blue-500 transition-colors"
            >
              About
            </a>
            <a 
              href="/#services" 
              onClick={(e) => handleSectionNavigation(e, 'services')} 
              className="hover:text-blue-500 transition-colors"
            >
              Services
            </a>
            <a 
              href="/#technologies" 
              onClick={(e) => handleSectionNavigation(e, 'technologies')} 
              className="hover:text-blue-500 transition-colors"
            >
              Technologies
            </a>
            <a 
              href="/#contact" 
              onClick={(e) => handleSectionNavigation(e, 'contact')} 
              className="hover:text-blue-500 transition-colors"
            >
              Contact
            </a>
          </div>
        </div>
      </nav>
      <main className="pt-28">{children}</main>
    </div>
  );
};

export default Layout;